/**
 * WHICH PAGES THIS SIGNED-IN PERSON MAY OPEN.
 *
 * Asked once per session from 2367 and held here, because two different things need the same
 * answer and neither should ask separately: the sidebar, to decide what to draw, and
 * PageAccessGuard, to stop a hidden page being reached by typing its URL.
 *
 * ── UNRESTRICTED IS THE DEFAULT, INCLUDING ON FAILURE ────────────────────────────────
 * `restricted` is false for the account owner and false again if the request itself fails. A
 * sub-user with no role is NOT unrestricted: they are denied every page (see noAccess). The last one is deliberate: this is a
 * convenience layer over a server that enforces its own rules, so a network blip must not blank
 * somebody's navigation. What actually protects an action is the capability check the gateway
 * runs on every call — this only decides what is worth offering.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { PagesService } from '@services/pages.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { SharedService } from '@services/shared.service';
import { toCampaignRoute } from '../utils/campaign-route.util';

export interface PageAccess {
  /**
   * Whether this is a real answer from the server, or the safe default.
   *
   * It matters because the default cannot be right about everything at once: it says
   * "unrestricted", which is the safe way to be wrong about which pages to DRAW, and it says
   * "not the owner", which is the unsafe way to be wrong about who may manage policies. A caller
   * that needs the second must know whether it is looking at an answer or at a placeholder.
   */
  loaded: boolean;
  /**
   * Whether this person IS the business. Not inferable from the two fields below — an owner and
   * a staff member nobody has restricted both report restricted:false with no policy — so the
   * server says it outright.
   */
  isOwner: boolean;
  /** Page keys — 'contests', 'billing', … — matching dashboard_pages. */
  pages: string[];
  /** The routes those pages live at, so a guard can answer without its own copy of the map. */
  urls: string[];
  /**
   * The subset of `pages` this person may CHANGE. A page present in `pages` but absent here is
   * read-only: it opens, and its create / edit / delete controls should not be offered.
   * Equal to `pages` for anyone unrestricted, so callers need no special case for the owner.
   */
  manage: string[];
  restricted: boolean;
  policy: { id: string; name: string } | null;
  /**
   * A sub-user who may open nothing: no role at all ("No Role (Access Denied)"), or a role that
   * grants no pages. They cannot work in the panel, so sign-in stops with a popup instead.
   */
  noAccess: boolean;
}

/**
 * User Management moved from the misspelt /home/user-managment to /home/user-management, and
 * the campaign pages from /home/contests… to /home/campaigns…. The server may still hand back
 * the old addresses, so both sides of a comparison are folded onto the new ones.
 */
function normalizeRoute(path: string): string {
  return toCampaignRoute(path.replace(/^\/home\/user-managment(?=\/|$)/, '/home/user-management'));
}

const UNRESTRICTED: PageAccess = {
  loaded: false, pages: [], urls: [], manage: [], isOwner: false, restricted: false, policy: null,
  noAccess: false,
};

@Injectable({ providedIn: 'root' })
export class PageAccessService {
  private pagesService = inject(PagesService);
  private secureStorage = inject(SecureStorageService);
  private sharedService = inject(SharedService);

  private access: PageAccess = UNRESTRICTED;
  private inflight?: Observable<PageAccess>;
  /** The session the cached answer belongs to. See load(). */
  private fetchedFor: string | null = null;

  /**
   * WHO the cached answer is about.
   *
   * The token, because it changes on every sign-in — a different person, or the same person
   * signing in again. Only its tail is kept: this is a cache key, not a credential, and there is
   * no reason to copy a whole token into another field.
   */
  private identity(): string {
    return String(this.secureStorage.getItem('authToken') ?? '').slice(-24);
  }

  /** Whether the stored account is a sub-user (set at sign-in from the LOGIN answer). */
  private isSubUser(): boolean {
    return this.secureStorage.getItem<any>('userDetails')?.is_sub_user === true;
  }

  /**
   * Load once and share. Several components ask on the same page load — the layout for its
   * sidebar and the guard for the first route — and shareReplay keeps that one request.
   */
  load(): Observable<PageAccess> {
    // KEYED ON THE SESSION, not cached for the life of the tab.
    //
    // This service is provided in root and shareReplay holds its answer, so without this the
    // FIRST answer of the browser session won. Sign in from the login screen — a single-page
    // navigation, no reload — and the answer already cached was the one fetched with no token
    // at all: refused by the gateway, caught, and filed as "unrestricted". The person then saw
    // every page in the dashboard however narrow their policy, and stayed that way until they
    // hard-refreshed. Same fault the other way: signing in as somebody else reused their
    // predecessor's answer.
    const who = this.identity();
    if (this.inflight && this.fetchedFor === who) return this.inflight;
    this.fetchedFor = who;

    this.inflight = this.pagesService.getAll('MY_PAGES').pipe(
      map((res: any) => {
        const d = res?.data ?? res ?? {};
        const access: PageAccess = {
          loaded: true,
          pages: Array.isArray(d.pages) ? d.pages : [],
          urls: Array.isArray(d.urls) ? d.urls : [],
          manage: Array.isArray(d.manage) ? d.manage : [],
          isOwner: d.is_owner === true,
          restricted: d.restricted === true,
          policy: d.policy ?? null,
          noAccess: false,
        };
        // A sub-user with no role came back unrestricted, so "No Role (Access Denied)" opened
        // every page (QA #289). A sub-user is denied until a role grants pages: no role, or a
        // role with no pages (QA #290), means nothing at all.
        if (!access.isOwner && this.isSubUser()) {
          if (!access.restricted && !access.policy) {
            access.restricted = true;
            access.pages = [];
            access.urls = [];
            access.manage = [];
          }
          access.noAccess = access.restricted && access.urls.length === 0;
        }
        return access;
      }),
      catchError(() => of(UNRESTRICTED)),
      tap((a) => (this.access = a)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inflight;
  }

  /** Forget the cached answer — after signing out, or after the owner changes a policy. */
  reset(): void {
    this.access = UNRESTRICTED;
    this.inflight = undefined;
    this.fetchedFor = null;
  }

  /**
   * End the session of a sub-user who may open no page, and say why in a popup. The caller
   * sends them to the sign-in screen.
   */
  signOutNoAccess(): void {
    this.secureStorage.clearProtected();
    localStorage.removeItem('profileImage');
    sessionStorage.clear();
    this.reset();
    this.sharedService.hideLoader();
    this.sharedService.createNotification('warning', 'login.no-page-access');
  }

  /**
   * The cached answer, but only while it is still about the person signed in now.
   *
   * Nothing resets this service on sign-out, so after a sub-user signed out and the owner signed
   * back in on the same tab the guard still held the sub-user's restricted answer — "loaded",
   * so it never asked again — and warned the owner off their own pages. An answer about somebody
   * else is treated as no answer: the guard then loads a fresh one for this session.
   */
  private get current(): PageAccess {
    return this.fetchedFor === this.identity() ? this.access : UNRESTRICTED;
  }

  get snapshot(): PageAccess {
    return this.current;
  }

  get restricted(): boolean {
    return this.current.restricted;
  }

  get isOwner(): boolean {
    return this.current.isOwner;
  }

  /** A sub-user with no page to open. See PageAccess.noAccess. */
  get noAccess(): boolean {
    return this.current.noAccess;
  }

  /** True once a real answer has arrived. See PageAccess.loaded. */
  get loaded(): boolean {
    return this.current.loaded;
  }

  /** May this person open the page with this key? Unrestricted people may open anything. */
  canOpen(key: string): boolean {
    if (!this.current.restricted) return true;
    return this.current.pages.includes(key);
  }

  /**
   * May this person CHANGE anything on that page?
   *
   * Use it to hide Create / Edit / Delete controls. It is not what stops a write — the gateway
   * refuses one on a read-only page whatever the browser does — but a button that leads only to
   * a refusal should not be offered in the first place.
   */
  canManage(key: string): boolean {
    if (!this.current.restricted) return true;
    return this.current.manage.includes(key);
  }

  /** True when this page opens but nothing on it may be changed. */
  isReadOnly(key: string): boolean {
    return this.canOpen(key) && !this.canManage(key);
  }

  /**
   * May this person open this URL?
   *
   * Matched by LONGEST allowed prefix, so a child route travels with its parent — someone who
   * has Engagements can open /home/campaigns/add and /home/campaigns/detail — while a page that
   * merely starts with the same letters cannot be reached by accident.
   *
   * The dashboard's own route is '/home' with nothing after it, which would otherwise prefix
   * every page in the panel, so it is matched exactly.
   */
  canOpenUrl(url: string): boolean {
    if (!this.current.restricted) return true;
    const path = normalizeRoute((url || '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/home');
    return this.current.urls.some((raw) => {
      const allowed = normalizeRoute((raw || '').replace(/\/+$/, ''));
      if (!allowed) return false;
      if (allowed === '/home') return path === '/home';
      return path === allowed || path.startsWith(allowed + '/');
    });
  }

  /** Where to send someone who landed somewhere they may not be. */
  get firstAllowedUrl(): string {
    return this.current.urls[0] || '/home';
  }
}
